import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useShowroomLocator from 'hooks/useShowroomLocator';
import useRetailStores from 'hooks/useRetailStores';
import useIsBrowser from 'hooks/useIsBrowser';
import useZipCodeFromUser from 'global-state/zipCodeFromUser/useZipCodeFromUser';
import AdminPlaceholder from 'components/consumer/AdminPlaceholder';
import StoreLocatorSearch from './StoreLocatorSearch';
import StoreLocatorResultsList from './StoreLocatorResultsList';
import StoreLocatorViewAll from './StoreLocatorViewAll';
import StoreLocatorNoResults from './StoreLocatorNoResults';
import StoreLocatorMap from './StoreLocatorMap';
import { isPhysicalShowroom, toStoreEntry } from './helpers';

// CON-8186: useShowroomLocator already strips Virtual Store from zipcode
// search results (covers header, footer, mobile nav). useRetailStores
// intentionally isn't filtered globally — other consumers (useCurrentShowroom,
// DynamicShowroomsList) may legitimately want the virtual record — so we
// strip it locally here for the View All list/map.

const VirtualShowroomBlock = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <h3 className="text-base text-brand font-bold mb-1">Virtual Showroom</h3>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <br />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mt-6 pt-4 border-t border-gray-light6 text-sm text-gray">{t0}<p>Cannot go to a showroom?{t1}Open the chat during business hours<br />or</p><a href="/showrooms/virtual/" data-xc="StoreLocatorVirtualShowroomLink" className="text-brand underline hover:text-gray-dark">Book a Virtual Showroom Appointment</a></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const StoreList = t0 => {
  const $ = _c(11);
  const {
    mode,
    allStores,
    allStoresLoading,
    allStoresError,
    searchStores,
    searchLoading,
    searchError,
    hasSearched,
    onActivate
  } = t0;
  if (mode === "viewAll") {
    if (allStoresLoading) {
      let t1;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = <div className="py-4 text-sm text-gray-light1">Loading showrooms...</div>;
        $[0] = t1;
      } else {
        t1 = $[0];
      }
      return t1;
    }
    if (allStoresError) {
      let t1;
      if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = <div className="py-4 text-sm text-gray-light1">Unable to load showrooms right now.</div>;
        $[1] = t1;
      } else {
        t1 = $[1];
      }
      return t1;
    }
    let t1;
    if ($[2] !== allStores || $[3] !== onActivate) {
      t1 = <StoreLocatorViewAll stores={allStores} onActivate={onActivate} />;
      $[2] = allStores;
      $[3] = onActivate;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  if (searchLoading) {
    let t1;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="py-4 text-sm text-gray-light1">Searching...</div>;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  if (searchError && (!searchStores || searchStores.length === 0)) {
    let t1;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="py-4 text-sm text-gray-light1">Unable to search right now. Please try again.</div>;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    return t1;
  }
  if (!hasSearched) {
    return null;
  }
  if (!searchStores || searchStores.length === 0) {
    let t1;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <StoreLocatorNoResults />;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    return t1;
  }
  let t1;
  if ($[8] !== onActivate || $[9] !== searchStores) {
    t1 = <StoreLocatorResultsList stores={searchStores} onActivate={onActivate} />;
    $[8] = onActivate;
    $[9] = searchStores;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
};
StoreList.propTypes = {
  mode: PropTypes.oneOf(['viewAll', 'search']).isRequired,
  allStores: PropTypes.array,
  allStoresLoading: PropTypes.bool,
  allStoresError: PropTypes.any,
  searchStores: PropTypes.array,
  searchLoading: PropTypes.bool,
  searchError: PropTypes.any,
  hasSearched: PropTypes.bool,
  onActivate: PropTypes.func.isRequired
};
const StoreLocatorPage = t0 => {
  const $ = _c(46);
  const {
    isAdmin: t1
  } = t0;
  const isAdmin = t1 === undefined ? false : t1;
  const storedZip = useZipCodeFromUser();
  const isBrowser = useIsBrowser();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      limit: 50
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    stores: searchStores,
    loading: searchLoading,
    error: searchError,
    hasSearched,
    handleSearch,
    handleViewAll
  } = useShowroomLocator(t2);
  const {
    stores: rawAllStores,
    loading: allStoresLoading,
    error: allStoresError
  } = useRetailStores();
  let t3;
  if ($[1] !== rawAllStores) {
    t3 = rawAllStores || [];
    $[1] = rawAllStores;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t3) {
    t4 = t3.filter(isPhysicalShowroom);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const allStores = t4;
  const autoSearchTriggeredRef = useRef(false);
  const [activeStoreId, setActiveStoreId] = useState(null);
  const [zipcode, setZipcode] = useState("");
  const mode = !isBrowser || !hasSearched ? "viewAll" : "search";
  let t5;
  let t6;
  if ($[5] !== handleSearch || $[6] !== hasSearched || $[7] !== storedZip) {
    t5 = () => {
      if (!autoSearchTriggeredRef.current && storedZip && !hasSearched) {
        setZipcode(storedZip);
        handleSearch(storedZip);
        autoSearchTriggeredRef.current = true;
      }
    };
    t6 = [storedZip, hasSearched, handleSearch];
    $[5] = handleSearch;
    $[6] = hasSearched;
    $[7] = storedZip;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  bb0: {
    if (mode === "viewAll") {
      let t8;
      if ($[10] !== allStores) {
        t8 = allStores || [];
        $[10] = allStores;
        $[11] = t8;
      } else {
        t8 = $[11];
      }
      t7 = t8;
      break bb0;
    }
    let t8;
    if ($[12] !== searchStores) {
      t8 = (searchStores || []).map(toStoreEntry).filter(Boolean).map(_temp);
      $[12] = searchStores;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    const searched = t8;
    if (searched.length === 0) {
      let t9;
      if ($[14] !== allStores) {
        t9 = allStores || [];
        $[14] = allStores;
        $[15] = t9;
      } else {
        t9 = $[15];
      }
      t7 = t9;
      break bb0;
    }
    t7 = searched;
  }
  const mapStores = t7;
  let t8;
  if ($[16] !== handleSearch) {
    t8 = nextZip => {
      setZipcode(nextZip);
      handleSearch(nextZip);
      setActiveStoreId(null);
    };
    $[16] = handleSearch;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const onSearch = t8;
  let t9;
  if ($[18] !== handleViewAll) {
    t9 = () => {
      setZipcode("");
      handleViewAll();
      setActiveStoreId(null);
    };
    $[18] = handleViewAll;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const onViewAll = t9;
  if (isAdmin) {
    let t10;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <AdminPlaceholder className="!min-h-[470px]">Store Locator Page Admin Placeholder</AdminPlaceholder>;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    return t10;
  }
  let t10;
  if ($[21] !== onSearch || $[22] !== onViewAll || $[23] !== zipcode) {
    t10 = <StoreLocatorSearch zipcode={zipcode} onZipcodeChange={setZipcode} onSearch={onSearch} onViewAll={onViewAll} />;
    $[21] = onSearch;
    $[22] = onViewAll;
    $[23] = zipcode;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== allStores || $[26] !== allStoresError || $[27] !== allStoresLoading || $[28] !== hasSearched || $[29] !== mode || $[30] !== searchError || $[31] !== searchLoading || $[32] !== searchStores) {
    t11 = <StoreList mode={mode} allStores={allStores} allStoresLoading={allStoresLoading} allStoresError={allStoresError} searchStores={searchStores} searchLoading={searchLoading} searchError={searchError} hasSearched={hasSearched} onActivate={setActiveStoreId} />;
    $[25] = allStores;
    $[26] = allStoresError;
    $[27] = allStoresLoading;
    $[28] = hasSearched;
    $[29] = mode;
    $[30] = searchError;
    $[31] = searchLoading;
    $[32] = searchStores;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <VirtualShowroomBlock />;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t11) {
    t13 = <aside className="w-full lg:w-80 lg:flex-shrink-0 px-4 py-4 lg:overflow-y-auto">{t11}{t12}</aside>;
    $[35] = t11;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== activeStoreId || $[38] !== mapStores) {
    t14 = <div className="flex-1 h-96 lg:h-full"><StoreLocatorMap stores={mapStores} activeStoreId={activeStoreId} onMarkerClick={setActiveStoreId} /></div>;
    $[37] = activeStoreId;
    $[38] = mapStores;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t13 || $[41] !== t14) {
    t15 = <div className="w-full flex flex-col lg:flex-row lg:flex-1 lg:min-h-0">{t13}{t14}</div>;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t10 || $[44] !== t15) {
    t16 = <section className="w-full flex flex-col lg:h-screen">{t10}{t15}</section>;
    $[43] = t10;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
};
StoreLocatorPage.propTypes = {
  isAdmin: PropTypes.bool
};
export default StoreLocatorPage;
function _temp(entry) {
  return entry.store;
}